import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiError } from "@/lib/api/errors";
import {
  type AnalysisResponse,
  type ExtractAcceptedResponse,
  getPolizzaResult,
  getPolizzaStatus,
  getRunResult,
  getRunStatus,
  type LlmProvider,
  type PolizzaExtractAcceptedResponse,
  type PolizzaExtractResponse,
  submitExtractMultipart,
  submitExtractPolizzaMultipart,
} from "@/lib/api/extract";
import { useLatest } from "@/lib/hooks/useLatest";
import {
  type ActiveRunTarget,
  type RunLinks,
  toUnifiedRunStatus,
  useCompletedResultRecovery,
  useRunPdfDownload,
  useRunPolling,
} from "./active-run-helpers";
import type { DocumentKind, UnifiedRunStatus } from "./status-domain";
import type { RefreshRunsResult } from "./useRunsHistory";

type SubmitExtractPayload = {
  file: File;
  documentKind: DocumentKind;
  async: boolean;
  pdfRenderDpi: number;
  codesLlmProvider: LlmProvider | null;
};

type RefreshRunsFn = (options?: {
  silent?: boolean;
}) => Promise<RefreshRunsResult>;

type UseActiveRunOptions = {
  isAuthenticated: boolean;
  onUnauthorized: (message?: string) => void;
  refreshRuns: RefreshRunsFn;
};

export type UseActiveRunResult = {
  runStatus: UnifiedRunStatus | null;
  analysis: AnalysisResponse | null;
  polizzaAnalysis: PolizzaExtractResponse | null;
  isSubmitting: boolean;
  isPollingActive: boolean;
  isPollingSyncing: boolean;
  downloadingPdfRunId: number | null;
  isFetchingCompletedResult: boolean;
  completedResultError: string | null;
  uploadErrorMessage: string | null;
  statusErrorMessage: string | null;
  clearUploadError: () => void;
  resetRunState: () => void;
  submitExtract: (
    payload: SubmitExtractPayload,
  ) => Promise<
    | AnalysisResponse
    | ExtractAcceptedResponse
    | PolizzaExtractResponse
    | PolizzaExtractAcceptedResponse
  >;
  inspectRun: (runId: number, kind: DocumentKind) => Promise<void>;
  downloadRunPdf: (runId: number, kind: DocumentKind) => Promise<void>;
  retryCompletedResult: () => Promise<void>;
  refreshRunsWithStatus: () => Promise<void>;
};

export function useActiveRun({
  isAuthenticated,
  onUnauthorized,
  refreshRuns,
}: UseActiveRunOptions): UseActiveRunResult {
  const [runStatus, setRunStatus] = useState<UnifiedRunStatus | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [polizzaAnalysis, setPolizzaAnalysis] =
    useState<PolizzaExtractResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingCompletedResult, setIsFetchingCompletedResult] =
    useState(false);
  const [completedResultError, setCompletedResultError] = useState<
    string | null
  >(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null,
  );
  const [statusErrorMessage, setStatusErrorMessage] = useState<string | null>(
    null,
  );
  const [runLinks, setRunLinks] = useState<RunLinks | null>(null);
  const onUnauthorizedRef = useLatest(onUnauthorized);
  const inspectRequestSeqRef = useRef(0);
  const activeRunRef = useRef<ActiveRunTarget | null>(null);
  const runLinksRef = useRef<RunLinks | null>(null);
  const notifyUnauthorized = useCallback(
    (message?: string) => {
      onUnauthorizedRef.current(message);
    },
    [onUnauthorizedRef],
  );
  const { downloadingPdfRunId, cachePdf, clearPdfCache, downloadRunPdf } =
    useRunPdfDownload({
      analysis,
      onUnauthorized: notifyUnauthorized,
      runStatus,
    });
  const {
    resetCompletedResultRecoveryState,
    startCompletedResultRecovery,
    stopCompletedResultRecovery,
  } = useCompletedResultRecovery({
    activeRunRef,
    onUnauthorized: notifyUnauthorized,
    setAnalysis,
    setPolizzaAnalysis,
    setCompletedResultError,
    setIsFetchingCompletedResult,
    cachePdf,
  });
  const { isPollingActive, isPollingSyncing, resetPollingState } =
    useRunPolling({
      isAuthenticated,
      onUnauthorized: notifyUnauthorized,
      refreshRuns,
      runLinks,
      runStatus,
      setAnalysis,
      setPolizzaAnalysis,
      setRunStatus,
      setStatusErrorMessage,
      startCompletedResultRecovery,
    });

  const resetRunState = useCallback(() => {
    setRunStatus(null);
    setAnalysis(null);
    setPolizzaAnalysis(null);
    setRunLinks(null);
    setUploadErrorMessage(null);
    setStatusErrorMessage(null);
    setIsFetchingCompletedResult(false);
    setCompletedResultError(null);
    setIsSubmitting(false);
    resetPollingState();
    activeRunRef.current = null;
    inspectRequestSeqRef.current += 1;
    setRunLinks(null);
    stopCompletedResultRecovery();
    clearPdfCache();
  }, [clearPdfCache, resetPollingState, stopCompletedResultRecovery]);

  const refreshActiveRunStatus = useCallback(async (): Promise<void> => {
    const activeTarget = activeRunRef.current;
    if (!activeTarget) return;

    const statusResponse =
      activeTarget.kind === "fatture_ddt"
        ? await getRunStatus(activeTarget.id)
        : await getPolizzaStatus(activeTarget.id);

    const nextStatus = toUnifiedRunStatus(activeTarget.kind, statusResponse);

    setRunStatus((currentStatus) => {
      if (
        currentStatus &&
        currentStatus.kind === nextStatus.kind &&
        currentStatus.id === nextStatus.id &&
        currentStatus.status === nextStatus.status &&
        currentStatus.inputFilename === nextStatus.inputFilename &&
        currentStatus.renderDpiUsed === nextStatus.renderDpiUsed &&
        currentStatus.totalPages === nextStatus.totalPages &&
        currentStatus.processedPages === nextStatus.processedPages &&
        currentStatus.failedPages === nextStatus.failedPages &&
        currentStatus.currentPage === nextStatus.currentPage &&
        currentStatus.progressPct === nextStatus.progressPct &&
        currentStatus.startedAt === nextStatus.startedAt &&
        currentStatus.finishedAt === nextStatus.finishedAt &&
        currentStatus.lastError === nextStatus.lastError
      ) {
        return currentStatus;
      }

      return nextStatus;
    });

    if (
      nextStatus.status === "completed" &&
      ((nextStatus.kind === "fatture_ddt" && !analysis) ||
        (nextStatus.kind === "polizza_carico" && !polizzaAnalysis))
    ) {
      startCompletedResultRecovery(
        nextStatus.kind,
        nextStatus.id,
        runLinksRef.current,
      );
    }
  }, [analysis, polizzaAnalysis, startCompletedResultRecovery]);

  const refreshRunsWithStatus = useCallback(async () => {
    try {
      const result = await refreshRuns();
      if (!result.ok) {
        if (result.errorMessage) {
          setStatusErrorMessage(result.errorMessage);
        }
        return;
      }

      await refreshActiveRunStatus();
      setStatusErrorMessage(null);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 401) {
        notifyUnauthorized();
        return;
      }
      setStatusErrorMessage(
        apiError.message ?? "Impossibile aggiornare lo stato dell'analisi.",
      );
    }
  }, [notifyUnauthorized, refreshActiveRunStatus, refreshRuns]);

  const retryCompletedResult = useCallback(async () => {
    if (!runStatus || runStatus.status !== "completed") return;
    setAnalysis(null);
    setPolizzaAnalysis(null);
    startCompletedResultRecovery(
      runStatus.kind,
      runStatus.id,
      runLinksRef.current,
    );
  }, [runStatus, startCompletedResultRecovery]);

  const clearUploadError = useCallback(() => {
    setUploadErrorMessage(null);
  }, []);

  const submitExtract = useCallback(
    async (
      payload: SubmitExtractPayload,
    ): Promise<
      | AnalysisResponse
      | ExtractAcceptedResponse
      | PolizzaExtractResponse
      | PolizzaExtractAcceptedResponse
    > => {
      try {
        resetCompletedResultRecoveryState();
        setIsSubmitting(true);
        setAnalysis(null);
        setPolizzaAnalysis(null);

        const response =
          payload.documentKind === "fatture_ddt"
            ? await submitExtractMultipart({
                file: payload.file,
                async: payload.async,
                pdfRenderDpi: payload.pdfRenderDpi,
                codesLlmProvider: payload.codesLlmProvider,
              })
            : await submitExtractPolizzaMultipart({
                file: payload.file,
                async: payload.async,
                pdfRenderDpi: payload.pdfRenderDpi,
                codesLlmProvider: payload.codesLlmProvider,
              });

        if ("status_url" in response) {
          const isFatture = payload.documentKind === "fatture_ddt";
          const id = isFatture
            ? (response as ExtractAcceptedResponse).run_id
            : (response as PolizzaExtractAcceptedResponse).polizza_id;

          setRunLinks({
            statusUrl: response.status_url,
            resultUrl: response.result_url,
          });
          setCompletedResultError(null);
          setIsFetchingCompletedResult(false);
          const queuedRun = toUnifiedRunStatus(payload.documentKind, {
            run_id: isFatture
              ? (response as ExtractAcceptedResponse).run_id
              : undefined,
            polizza_id: isFatture
              ? undefined
              : (response as PolizzaExtractAcceptedResponse).polizza_id,
            status: "queued",
            input_filename: payload.file.name,
            render_dpi_used: null,
            total_pages: 0,
            processed_pages: 0,
            failed_pages: 0,
            current_page: null,
            progress_pct: 0,
            started_at: null,
            finished_at: null,
            last_error: null,
          });
          setRunStatus(queuedRun);
          activeRunRef.current = { id, kind: payload.documentKind };
        } else {
          setRunLinks(null);
          setCompletedResultError(null);
          setIsFetchingCompletedResult(false);

          if (payload.documentKind === "fatture_ddt") {
            const fattureResult = response as AnalysisResponse;
            setAnalysis(fattureResult);
            setPolizzaAnalysis(null);
            if (fattureResult.run_id && fattureResult.pdf_base64) {
              cachePdf(fattureResult.run_id, fattureResult.pdf_base64);
            }
            if (fattureResult.run_id) {
              const completedRun = toUnifiedRunStatus("fatture_ddt", {
                run_id: fattureResult.run_id,
                status: "completed",
                input_filename: fattureResult.input_filename,
                total_pages: fattureResult.pages.length,
                processed_pages: fattureResult.pages.length,
                failed_pages: fattureResult.page_failures.length,
                progress_pct: 100,
                started_at: null,
                finished_at: null,
                last_error: null,
              });
              setRunStatus(completedRun);
              activeRunRef.current = {
                id: fattureResult.run_id,
                kind: "fatture_ddt",
              };
            } else {
              setRunStatus(null);
            }
          } else {
            const polizzaResult = response as PolizzaExtractResponse;
            setPolizzaAnalysis(polizzaResult);
            setAnalysis(null);
            if (polizzaResult.polizza_id) {
              const completedRun = toUnifiedRunStatus("polizza_carico", {
                polizza_id: polizzaResult.polizza_id,
                status: "completed",
                input_filename: polizzaResult.input_filename,
                total_pages: polizzaResult.pages.length,
                processed_pages: polizzaResult.pages.length,
                failed_pages: polizzaResult.page_failures.length,
                progress_pct: 100,
                started_at: null,
                finished_at: null,
                last_error: null,
              });
              setRunStatus(completedRun);
              activeRunRef.current = {
                id: polizzaResult.polizza_id,
                kind: "polizza_carico",
              };
            } else {
              setRunStatus(null);
            }
          }
        }

        const refreshResult = await refreshRuns();
        if (refreshResult.ok) {
          setStatusErrorMessage(null);
        } else if (refreshResult.errorMessage) {
          setStatusErrorMessage(refreshResult.errorMessage);
        }

        setUploadErrorMessage(null);
        return response;
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          notifyUnauthorized();
          throw error;
        }
        setUploadErrorMessage(
          apiError.message ?? "Errore imprevisto durante l'invio.",
        );
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      cachePdf,
      notifyUnauthorized,
      refreshRuns,
      resetCompletedResultRecoveryState,
    ],
  );

  const inspectRun = useCallback(
    async (runId: number, kind: DocumentKind): Promise<void> => {
      const inspectSeq = inspectRequestSeqRef.current + 1;
      inspectRequestSeqRef.current = inspectSeq;
      try {
        resetCompletedResultRecoveryState();
        setRunLinks(null);

        const status =
          kind === "fatture_ddt"
            ? await getRunStatus(runId)
            : await getPolizzaStatus(runId);

        if (inspectRequestSeqRef.current !== inspectSeq) return;
        const unified = toUnifiedRunStatus(kind, status);
        activeRunRef.current = { id: runId, kind };

        if (status.status === "completed") {
          try {
            if (kind === "fatture_ddt") {
              const nextAnalysis = await getRunResult(runId);
              if (inspectRequestSeqRef.current !== inspectSeq) return;
              if (nextAnalysis.pdf_base64) {
                cachePdf(runId, nextAnalysis.pdf_base64);
              }
              setRunStatus(unified);
              setAnalysis(nextAnalysis);
              setPolizzaAnalysis(null);
            } else {
              const nextPolizzaAnalysis = await getPolizzaResult(runId);
              if (inspectRequestSeqRef.current !== inspectSeq) return;
              setRunStatus(unified);
              setPolizzaAnalysis(nextPolizzaAnalysis);
              setAnalysis(null);
            }

            setCompletedResultError(null);
            setIsFetchingCompletedResult(false);
          } catch (error) {
            if (inspectRequestSeqRef.current !== inspectSeq) return;
            const apiError = error as ApiError;
            if (apiError.status === 401) {
              notifyUnauthorized();
              return;
            }
            setRunStatus(unified);
            setAnalysis(null);
            setPolizzaAnalysis(null);
            startCompletedResultRecovery(kind, runId, null);
          }
        } else {
          setRunStatus(unified);
          setAnalysis(null);
          setPolizzaAnalysis(null);
          setCompletedResultError(null);
          setIsFetchingCompletedResult(false);
        }

        setStatusErrorMessage(null);
      } catch (error) {
        if (inspectRequestSeqRef.current !== inspectSeq) return;
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          notifyUnauthorized();
          return;
        }
        setStatusErrorMessage(
          apiError.message ?? "Impossibile caricare i dettagli dell'analisi.",
        );
      }
    },
    [
      cachePdf,
      notifyUnauthorized,
      resetCompletedResultRecoveryState,
      startCompletedResultRecovery,
    ],
  );

  useEffect(() => {
    runLinksRef.current = runLinks;
  }, [runLinks]);

  return {
    runStatus,
    analysis,
    polizzaAnalysis,
    isSubmitting,
    isPollingActive,
    isPollingSyncing,
    downloadingPdfRunId,
    isFetchingCompletedResult,
    completedResultError,
    uploadErrorMessage,
    statusErrorMessage,
    clearUploadError,
    resetRunState,
    submitExtract,
    inspectRun,
    downloadRunPdf,
    retryCompletedResult,
    refreshRunsWithStatus,
  };
}
